import type { Node } from 'react'
import React, { useEffect, useState } from 'react'
import {
  Button, TextField, Typography,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers'
import { subHours, differenceInHours } from 'date-fns'
import { format as formatTz } from 'date-fns-tz'

import PropTypes from '@app/components/PropTypes'
import useStyles from './TimePicker.style'

type Props = {
  onSubmitHandler: (startDate: Date, endDate: Date, timezone: string) => Promise<any>,
}

const guessTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

const getOffsetLabel = (tz: string): string => {
  try {
    return formatTz(new Date(), 'XXX', { timeZone: tz })
  } catch {
    return '+00:00'
  }
}

const formatTimezoneOption = (tz: string): string => `(GMT ${getOffsetLabel(tz)}) ${tz.replace(/_/g, ' ')}`

const TimePicker = (props: Props): Node => {
  const {
    onSubmitHandler,
  } = props
  const { classes } = useStyles()
  const guessed = guessTimezone()
  const [dateRange, setDateRange] = useState({
    startDate: subHours(new Date(), 12),
    endDate: new Date(),
    timezone: formatTimezoneOption(guessed),
  })
  const [placeholderValue, setPlaceholderValue] = useState(dateRange.timezone)

  const timezones = Intl.supportedValuesOf('timeZone')
  const offsetTmz = timezones.map((tz) => formatTimezoneOption(tz))

  const startDateChangeHandler = (newDate: Date | null): void => {
    if (newDate) {
      setDateRange((prevRange) => ({
        ...prevRange,
        startDate: newDate,
      }))
    }
  }

  const endDateChangeHandler = (newDate: Date | null): void => {
    if (newDate && dateRange.startDate < newDate) {
      setDateRange((prevRange) => ({
        ...prevRange,
        endDate: newDate,
      }))
    }
  }

  const changeTimezoneHandler = (timezone: string): void => {
    setDateRange((prevRange) => ({
      ...prevRange,
      timezone,
    }))
  }

  useEffect(() => {
    onSubmitHandler(new Date(dateRange.startDate), new Date(dateRange.endDate), dateRange.timezone.replace(/ /g, '_'))
  }, [])

  return (
    <>
      <div className={classes.root}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            ampm={false}
            label="Start date"
            value={dateRange.startDate}
            onChange={startDateChangeHandler}
            slotProps={{
              textField: {
                variant: 'standard',
                className: classes.timePicker,
              },
            }}
          />
          <DateTimePicker
            ampm={false}
            label="End date"
            value={dateRange.endDate}
            onChange={endDateChangeHandler}
            slotProps={{
              textField: {
                variant: 'standard',
                className: classes.timePicker,
              },
            }}
          />
          <Autocomplete
            classes={{
              root: classes.autocomplete,
            }}
            options={offsetTmz}
            id="timezone-select"
            getOptionLabel={(option) => option}
            inputValue={placeholderValue}
            onInputChange={(_, tz) => setPlaceholderValue(tz)}
            value={dateRange.timezone}
            onChange={(_, tz) => changeTimezoneHandler(tz)}
            renderInput={(params) => (
              <TextField
                variant="standard"
                {...params}
                label="Choose your timezone"
                className={classes.timezoneSelect}
              />
            )}
          />
          <Button
            size="large"
            color="primary"
            variant="contained"
            onClick={() => onSubmitHandler(
              new Date(dateRange.startDate),
              new Date(dateRange.endDate),
              dateRange.timezone.replace(/ /g, '_'),
            )}
          >
            Show
          </Button>
        </LocalizationProvider>

      </div>
      <div>
        <Typography className={classes.difference}>
          {`Stats for ${differenceInHours(dateRange.endDate, dateRange.startDate)} hours according to your filters`}
        </Typography>
      </div>
    </>
  )
}

TimePicker.propTypes = {
  onSubmitHandler: PropTypes.func.isRequired,
}

export default TimePicker
